"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Scissors } from "lucide-react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import Navbar from "../../../components/navbar";
import Footer from "../../../components/footer";
import Button from "../../../components/Button";
import styles from "../css/rpDebtorStatement.module.css";
import api from "../../../api";

const pad2 = (n) => (n < 10 ? `0${n}` : String(n));
const formatDMY = (d) => `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;

const formatMoney = (n) => {
  const num = Number(n) || 0;
  return `R ${num.toFixed(2)}`;
};

const getDebitAmount = (tx) => tx?.debit ?? tx?.debt ?? 0;

export default function RpDebtorStatement() {
  const navigate = useNavigate();
  const { profileNo } = useParams();
  const sheetRef = useRef(null);
  const [statementData, setStatementData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStatement = async () => {
      if (!profileNo) return;
      try {
        setLoading(true);
        console.log("Fetching statement for profileNo:", profileNo);
        const res = await api.get(`/rp-debtors/statement/${profileNo}`);
        console.log("API response:", res.data);
        if (res.data?.success) {
          console.log("Setting statement data:", res.data.data);
          setStatementData(res.data.data);
        } else {
          setError("Failed to load statement data: " + (res.data?.error || "Unknown error"));
        }
      } catch (err) {
        console.error("Failed to fetch debtor statement:", err);
        setError(err?.message || "Failed to load statement");
      } finally {
        setLoading(false);
      }
    };

    fetchStatement();
  }, [profileNo]);

  const statementMeta = useMemo(() => {
    if (!statementData) {
      // Default fallback while loading
      const now = new Date();
      const statementDate = new Date(now.getFullYear(), now.getMonth(), 15);
      return {
        statementDate: formatDMY(statementDate),
        accountNo: "ACC-00462",
        terms: "",
        from: {
          name: "Durban North Pharmacy",
          line3: "35 Umhlanga Rocks Drive",
          line4: "Durban North",
        },
        customer: {
          name: "Mrs. Priya Naidoo",
          line2: "12 Umhlanga Ridge Boulevard",
          line3: "Umhlanga, Durban, 4319",
          line4: "Durban",
          tel: "084 764 6850",
          vat: "",
        },
      };
    }

    return {
      statementDate: statementData.statementDate,
      accountNo: statementData.accountNo,
      terms: statementData.terms || "",
      from: statementData.from,
      customer: statementData.customer,
    };
  }, [statementData]);

  const statementRows = useMemo(() => {
    if (!statementData?.transactions?.length) {
      return [];
    }

    return statementData.transactions.map((tx) => ({
      date: tx.date,
      reference: tx.reference,
      debit: getDebitAmount(tx) ? formatMoney(getDebitAmount(tx)) : "",
      credit: tx.credit ? formatMoney(tx.credit) : "-",
      balance: formatMoney(tx.balance),
    }));
  }, [statementData]);

  const totalBalance = useMemo(
    () => statementData?.totalBalance || 0,
    [statementData]
  );

  const ageAnalysis = useMemo(
    () =>
      statementData?.ageAnalysis || [
        { label: "Current", value: 0 },
        { label: "30 Days", value: 0 },
        { label: "60 Days", value: 0 },
        { label: "90 Days", value: 0 },
        { label: "120+ Days", value: 0 },
        { label: "150+ Days", value: 0 },
      ],
    [statementData]
  );

  const paymentSummaryRows = useMemo(() => {
    if (!statementData?.transactions?.length) {
      return [];
    }

    const firstTx = statementData.transactions[0];
    const openingBalance =
      (Number(firstTx?.balance) || 0) - (getDebitAmount(firstTx) || 0) + (Number(firstTx?.credit) || 0);

    const rows = [{ label: "Balance Brought Forward", value: formatMoney(openingBalance) }];
    statementData.transactions.forEach((tx) => {
      rows.push({
        label: tx.reference,
        value: getDebitAmount(tx) ? formatMoney(getDebitAmount(tx)) : `-${formatMoney(tx.credit || 0)}`,
      });
    });
    return rows;
  }, [statementData]);

  const lastPaymentDate = useMemo(
    () => statementData?.lastPaymentDate || "",
    [statementData]
  );

  const handleDownload = async () => {
    if (!sheetRef.current) return;

    try {
      const canvas = await html2canvas(sheetRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "px",
        format: "a4",
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 20;

      pdf.addImage(imgData, "PNG", imgX, imgY, imgWidth * ratio, imgHeight * ratio);
      pdf.save(`Debtor_Statement_${profileNo || "ACC-00462"}.pdf`);
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  return (
    <div className={styles.wrapper}>
      <Navbar
        backTo="/credits/rpdebtors"
        onBack={() => navigate("/credits/rpdebtors")}
      />

      <main className={styles.main}>
        {loading && (
          <div className={styles.loadingState}>Loading statement...</div>
        )}
        {error && (
          <div className={styles.errorState}>Error: {error}</div>
        )}
        {!loading && !error && (
          <>
            <div className={styles.sheet} ref={sheetRef}>
          <div className={styles.grid}>
            <section className={styles.card}>
              <div className={styles.cardHeader}>DEBTORS STATEMENT</div>

              <div className={styles.cardBody}>
                <div className={styles.metaRow}>
                  <div className={styles.block}>
                    <div className={styles.blockTitle}>FROM:</div>
                    {statementMeta.from.line1 && (
                      <div className={styles.blockLine}>{statementMeta.from.line1}</div>
                    )}
                    <div className={styles.blockLine}>{statementMeta.from.name}</div>
                    <div className={styles.blockLine}>{statementMeta.from.line3}</div>
                    <div className={styles.blockLine}>{statementMeta.from.line4}</div>
                  </div>

                  <div className={styles.metaRight}>
                    <div className={styles.metaPair}>
                      <span>Statement Date:</span>
                      <span>{statementMeta.statementDate}</span>
                    </div>
                    <div className={styles.metaPair}>
                      <span>Account No:</span>
                      <span>{statementMeta.accountNo}</span>
                    </div>
                    <div className={styles.metaPair}>
                      <span>Terms:</span>
                      <span>{statementMeta.terms}</span>
                    </div>
                  </div>
                </div>

                <div className={styles.blockDivider} />

                <div className={styles.block}>
                  <div className={styles.blockTitle}>CUSTOMER</div>
                  <div className={styles.blockLine}>{statementMeta.customer.name}</div>
                  <div className={styles.blockLine}>{statementMeta.customer.line2}</div>
                  <div className={styles.blockLine}>{statementMeta.customer.line3}</div>
                  <div className={styles.blockLine}>{statementMeta.customer.line4}</div>
                  <div className={styles.blockLine}>Tel: {statementMeta.customer.tel}</div>
                  {statementMeta.customer.vat ? (
                    <div className={styles.blockLine}>VAT: {statementMeta.customer.vat}</div>
                  ) : null}
                </div>

                <div className={styles.tableCard}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Reference</th>
                        <th className={styles.moneyHeader}>Debit</th>
                        <th className={styles.moneyHeader}>Credit</th>
                        <th className={styles.balanceHeader}>Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statementRows.map((row, idx) => (
                        <tr key={`${row.reference}-${idx}`}>
                          <td>{row.date}</td>
                          <td>{row.reference}</td>
                          <td className={styles.moneyCell}>{row.debit}</td>
                          <td className={styles.moneyCell}>{row.credit}</td>
                          <td className={styles.balanceCell}>{row.balance}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div className={styles.totalRow}>
                    <div className={styles.totalLabel}>TOTAL BALANCE DUE</div>
                    <div className={styles.totalValue}>{formatMoney(totalBalance)}</div>
                  </div>
                </div>

                <div className={styles.ageSection}>
                  <div className={styles.ageTitle}>AGE ANALYSIS</div>
                  <div className={styles.ageGrid}>
                    {ageAnalysis.map((a) => (
                      <div key={a.label} className={styles.ageBox}>
                        <div className={styles.ageLabel}>{a.label}</div>
                        <div className={styles.ageValue}>{formatMoney(a.value)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            <div className={styles.gridCutDivider}>
              <div className={styles.gridCutLine} />
              <div className={styles.gridCutIcon}>
                <Scissors size={14} />
              </div>
            </div>

            <section className={styles.card}>
              <div className={styles.cardHeader}>REMITTANCE ADVICE</div>

              <div className={styles.cardBody}>
                <div className={styles.block}>
                  <div className={styles.blockTitle}>FROM:</div>
                  {statementMeta.from.line1 && (
                    <div className={styles.blockLine}>{statementMeta.from.line1}</div>
                  )}
                  <div className={styles.blockLine}>{statementMeta.from.name}</div>
                  <div className={styles.blockLine}>{statementMeta.from.line2}</div>
                  <div className={styles.blockLine}>{statementMeta.from.line3}</div>
                  <div className={styles.blockLine}>{statementMeta.from.line4}</div>
                </div>

                <div className={styles.remitMetaRow}>
                  <div className={styles.block}>
                    <div className={styles.blockTitle}>CUSTOMER</div>
                    <div className={styles.blockLine}>{statementMeta.customer.name}</div>
                    <div className={styles.blockLine}>{statementMeta.customer.line2}</div>
                    <div className={styles.blockLine}>{statementMeta.customer.line3}</div>
                    <div className={styles.blockLine}>{statementMeta.customer.line4}</div>
                    <div className={styles.blockLine}>Tel: {statementMeta.customer.tel}</div>
                  </div>

                  <div className={styles.metaRight}>
                    <div className={styles.metaPair}>
                      <span>Statement Date:</span>
                      <span>{statementMeta.statementDate}</span>
                    </div>
                    <div className={styles.metaPair}>
                      <span>Account No:</span>
                      <span>{statementMeta.accountNo}</span>
                    </div>
                  </div>
                </div>

                <div className={styles.paymentSummary}>
                  <div className={styles.paymentTitle}>PAYMENT SUMMARY</div>
                  <div className={styles.paymentTable}>
                    {paymentSummaryRows.map((r, i) => (
                      <div key={`${r.label}-${i}`} className={styles.paymentRow}>
                        <span>{r.label}</span>
                        <span className={styles.paymentValue}>{r.value}</span>
                      </div>
                    ))}
                  </div>

                  <div className={styles.paymentTotal}>
                    <div className={styles.paymentTotalLabel}>TOTAL BALANCE DUE:</div>
                    <div className={styles.paymentTotalValue}>{formatMoney(totalBalance)}</div>
                  </div>

                  <div className={styles.paymentFootnote}>
                    <div className={styles.footnoteRow}>
                      <span>Last Payment Date:</span>
                      <span>{lastPaymentDate}</span>
                    </div>
                    <div className={styles.footnoteRow}>
                      <span>SETT-Discount</span>
                      <span>{formatMoney(0)}</span>
                    </div>
                  </div>
                </div>

                <div className={styles.returnTitle}>RETURN WITH PAYMENT</div>

                <div className={styles.bankCard}>
                  <div className={styles.bankTitle}>BANKING DETAILS</div>
                  <div className={styles.bankGrid}>
                    <div className={styles.bankRow}><span>Bank Name:</span><span>Standard Bank</span></div>
                    <div className={styles.bankRow}><span>Branch:</span><span>Tongaat</span></div>
                    <div className={styles.bankRow}><span>Branch Code:</span><span>057729</span></div>
                    <div className={styles.bankRow}><span>Account Number</span><span>032723601</span></div>
                    <div className={styles.bankRow}><span>Account Name:</span><span>EDONOX t/a Durban North Pharmacy</span></div>
                  </div>
                </div>

                <div className={styles.profileHint}>Profile No: {profileNo}</div>
              </div>
            </section>
          </div>
        </div>

        <div className={styles.downloadRow}>
          <Button
            variant="primary"
            type="button"
            className={styles.downloadBtn}
            onClick={handleDownload}
          >
            Download
          </Button>
        </div>
        </>
        )}
      </main>

      <Footer />
    </div>
  );
}
